import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { io } from 'socket.io-client';
import {
  Truck, Navigation, Activity, Wifi, WifiOff,
  Clock, Gauge, Radio, Satellite, MapPin, RefreshCw,
  Play, Pause, Square, FastForward, RotateCcw, Calendar,
  Bell, Shield, Settings, AlertTriangle, CheckCircle, X,
  Plus, Trash2, Link, ChevronDown
} from 'lucide-react';
import api from '../services/api';

// ─── Fix ícones padrão do Leaflet no Vite ────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ─── Ícone customizado do veículo ─────────────────────────────────────────────
function makeTruckIcon(isMoving, isOffline, color = null) {
  const c = color || (isOffline ? '#9ca3af' : isMoving ? '#16a34a' : '#2563eb');
  const pulse = isMoving && !isOffline
    ? `<span style="position:absolute;top:-4px;left:-4px;width:32px;height:32px;border-radius:50%;background:${c};opacity:0.25;animation:ping 1.5s ease-in-out infinite;"></span>`
    : '';
  return L.divIcon({
    html: `
      <style>@keyframes ping{0%,100%{transform:scale(1);opacity:.25}50%{transform:scale(1.5);opacity:0;}}</style>
      <div style="position:relative;width:24px;height:24px;">
        ${pulse}
        <div style="width:24px;height:24px;border-radius:50%;background:${c};border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;">
          <svg viewBox="0 0 24 24" fill="white" width="13" height="13"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>
        </div>
      </div>`,
    className: '',
    iconSize:   [24, 24],
    iconAnchor: [12, 12],
    popupAnchor:[0, -14],
  });
}

// ─── Componente para centralizar mapa em posição ──────────────────────────────
function MapFlyTo({ position, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom, { duration: 1 });
  }, [position, zoom, map]);
  return null;
}

function MapFitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 1) {
      const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

// ─── Utilitários ─────────────────────────────────────────────────────────────
function formatAgo(timestamp) {
  if (!timestamp) return '—';
  const str = String(timestamp);
  const ts = (str.endsWith('Z') || str.includes('+')) ? str : str + 'Z';
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60)  return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
  return `${Math.floor(diff / 3600)}h atrás`;
}

function formatTime(timestamp) {
  if (!timestamp) return '—';
  const str = String(timestamp);
  const ts = (str.endsWith('Z') || str.includes('+')) ? str : str + 'Z';
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

function isOffline(timestamp, intervalSec = 60) {
  if (!timestamp) return true;
  return (Date.now() - new Date(timestamp)) / 1000 > (intervalSec * 3);
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const SPEEDS  = [1, 2, 5, 10, 30];

// ─── Cores para geofences ─────────────────────────────────────────────────────
const ZONE_COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4'];

// ─── Mapa de tipos de alerta ─────────────────────────────────────────────────
const ALERT_META = {
  speed:              { label: 'Velocidade', icon: '🏎', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800' },
  offline:            { label: 'Offline',    icon: '📡', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-600' },
  idle_outside_zone:  { label: 'Local indesejado', icon: '📍', color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800' },
  outside_hours:      { label: 'Fora de horário', icon: '🕐', color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800' },
  geofence_entry:     { label: 'Entrada zona', icon: '🟢', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800' },
  geofence_exit:      { label: 'Saída zona',   icon: '🔴', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800' },
};

// ─── Componente para capturar click no mapa (criar geofence) ─────────────────
function MapClickCapture({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
export default function TrackingPage() {
  const [view, setView]                 = useState('map'); // 'map' | 'list' | 'replay' | 'zones' | 'alerts'

  // ── Live tracking state ──────────────────────────────────────────────────
  const [vehicles, setVehicles]         = useState({});

  // ── Zones state ──────────────────────────────────────────────────────────
  const [geofences, setGeofences]       = useState([]);
  const [drawingMode, setDrawingMode]   = useState(false);
  const [newZoneForm, setNewZoneForm]   = useState({ name: '', radius_meters: 500, color: '#3B82F6', lat: null, lng: null });
  const [selectedZone, setSelectedZone] = useState(null);
  const [linkVehicleId, setLinkVehicleId] = useState('');
  const [linkAllowed, setLinkAllowed]   = useState(true);
  const [linkAlertEntry, setLinkAlertEntry] = useState(false);
  const [linkAlertExit, setLinkAlertExit]   = useState(false);
  const [zoneVehicles, setZoneVehicles] = useState([]);

  // ── Alerts state ─────────────────────────────────────────────────────────
  const [alerts, setAlerts]             = useState([]);
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [alertFilter, setAlertFilter]   = useState({ type: '', resolved: 'false' });

  // ── Monitor config modal ─────────────────────────────────────────────────
  const [monitorVehicleId, setMonitorVehicleId] = useState(null);
  const [monitorConfig, setMonitorConfig]       = useState(null);
  const [blockingImei, setBlockingImei]         = useState(null); // IMEI sendo bloqueado/desbloqueado
  const [selectedId, setSelectedId]     = useState(null);
  const [flyTo, setFlyTo]               = useState(null);
  const [socketOk, setSocketOk]         = useState(false);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const socketRef = useRef(null);

  // ── Replay state ─────────────────────────────────────────────────────────
  const [replayVehicleId, setReplayVehicleId] = useState('');
  const [replayDate, setReplayDate]           = useState(todayStr());
  const [replayPoints, setReplayPoints]       = useState([]);
  const [replayStats, setReplayStats]         = useState(null);
  const [replayIdx, setReplayIdx]             = useState(0);
  const [replayPlaying, setReplayPlaying]     = useState(false);
  const [replaySpeed, setReplaySpeed]         = useState(5);
  const [replayLoading, setReplayLoading]     = useState(false);
  const replayTimer = useRef(null);
  const replayIdxRef = useRef(0);

  // ── Bloqueio/Desbloqueio remoto ─────────────────────────────────────────
  const sendBlockCmd = useCallback(async (imei, block) => {
    if (!imei) { toast.error('IMEI não disponível'); return; }
    const action = block ? 'bloquear' : 'desbloquear';
    if (!window.confirm(`Confirma ${action} o veículo com IMEI ${imei}?`)) return;
    setBlockingImei(imei);
    try {
      await api.post(`/api/tracking/gps-devices/${imei}/command`, { cmd: block ? 'block' : 'unblock' });
      toast.success(`Comando de ${action} enviado!`);
      // Atualiza estado local imediatamente
      setVehicles(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(k => {
          if (updated[k].imei === imei) updated[k] = { ...updated[k], is_blocked: block };
        });
        return updated;
      });
    } catch (err) {
      toast.error(`Erro ao ${action}: ${err.response?.data?.message || err.message}`);
    } finally {
      setBlockingImei(null);
    }
  }, []);

  // ── Carrega posições iniciais via REST ──────────────────────────────────
  const loadInitial = useCallback(async () => {
    try {
      const res = await api.get('/api/tracking/vehicles/current-locations');
      const map = {};
      for (const v of (res.data.data || [])) map[v.vehicle_id] = v;
      setVehicles(map);
    } catch (e) {
      console.error('Erro ao carregar localizações:', e);
    }
  }, []);

  // ── Zones ─────────────────────────────────────────────────────────────────
  const loadGeofences = useCallback(async () => {
    try {
      const res = await api.get('/api/tracking/geofences');
      setGeofences(res.data.data || []);
    } catch (e) { console.error('Erro ao carregar zonas:', e); }
  }, []);

  async function createGeofence() {
    const { name, radius_meters, color, lat, lng } = newZoneForm;
    if (!name || !lat) return;
    await api.post('/api/tracking/geofences', {
      name, type: 'circular', center_latitude: lat, center_longitude: lng,
      radius_meters: parseInt(radius_meters), color
    });
    setNewZoneForm({ name: '', radius_meters: 500, color: '#3B82F6', lat: null, lng: null });
    setDrawingMode(false);
    loadGeofences();
  }

  async function deleteGeofence(id) {
    if (!confirm('Excluir esta zona?')) return;
    await api.delete(`/api/tracking/geofences/${id}`);
    loadGeofences();
    if (selectedZone?.id === id) setSelectedZone(null);
  }

  async function loadZoneVehicles(geofenceId) {
    try {
      const res = await api.get(`/api/tracking/geofences/${geofenceId}/vehicles`).catch(() => ({ data: { data: [] } }));
      setZoneVehicles(res.data.data || []);
    } catch { setZoneVehicles([]); }
  }

  async function linkVehicleToZone(geofenceId) {
    if (!linkVehicleId) return;
    await api.post('/api/tracking/vehicle-geofences', {
      vehicle_id: linkVehicleId, geofence_id: geofenceId,
      is_allowed_zone: linkAllowed, alert_on_entry: linkAlertEntry, alert_on_exit: linkAlertExit
    });
    setLinkVehicleId('');
    loadZoneVehicles(geofenceId);
  }

  // ── Alerts ────────────────────────────────────────────────────────────────
  const loadAlerts = useCallback(async () => {
    try {
      const params = {};
      if (alertFilter.type) params.alert_type = alertFilter.type;
      if (alertFilter.resolved !== '') params.is_resolved = alertFilter.resolved;
      const res = await api.get('/api/tracking/alerts', { params });
      setAlerts(res.data.data || []);
    } catch (e) { console.error('Erro ao carregar alertas:', e); }
  }, [alertFilter]);

  const loadAlertsSummary = useCallback(async () => {
    try {
      const res = await api.get('/api/tracking/alerts/summary');
      setAlertsUnread(res.data.data?.total || 0);
    } catch { }
  }, []);

  async function markAlertRead(id) {
    await api.put(`/api/tracking/alerts/${id}/read`);
    loadAlerts(); loadAlertsSummary();
  }

  async function resolveAlert(id) {
    await api.put(`/api/tracking/alerts/${id}/resolve`);
    loadAlerts(); loadAlertsSummary();
  }

  async function readAllAlerts() {
    await api.put('/api/tracking/alerts/read-all');
    loadAlerts(); loadAlertsSummary();
  }

  // ── Monitor config ─────────────────────────────────────────────────────────
  async function openMonitorModal(vehicleId) {
    setMonitorVehicleId(vehicleId);
    const res = await api.get(`/api/tracking/vehicle-monitoring/${vehicleId}`);
    setMonitorConfig(res.data.data);
  }

  async function saveMonitorConfig() {
    await api.put(`/api/tracking/vehicle-monitoring/${monitorVehicleId}`, monitorConfig);
    setMonitorVehicleId(null);
  }

  // ── Zones + Alerts load on tab switch ─────────────────────────────────────
  useEffect(() => {
    if (view === 'zones') loadGeofences();
    if (view === 'alerts') { loadAlerts(); loadAlertsSummary(); }
  }, [view, loadGeofences, loadAlerts, loadAlertsSummary]);

  useEffect(() => {
    loadAlertsSummary();
    const interval = setInterval(loadAlertsSummary, 20 * 60 * 1000); // a cada 20 min
    return () => clearInterval(interval);
  }, [loadAlertsSummary]);
  useEffect(() => { if (view === 'alerts') loadAlerts(); }, [alertFilter, view, loadAlerts]);

  // ── Socket.io ────────────────────────────────────────────────────────────
  useEffect(() => {
    loadInitial();
    const socket = io(API_URL, { transports: ['websocket'] });
    socketRef.current = socket;
    socket.on('connect',    () => setSocketOk(true));
    socket.on('disconnect', () => setSocketOk(false));
    socket.on('gps:update', (data) => {
      if (!data.vehicle_id) return;
      setVehicles(prev => ({
        ...prev,
        [data.vehicle_id]: {
          ...(prev[data.vehicle_id] || {}),
          vehicle_id: data.vehicle_id, latitude: data.latitude, longitude: data.longitude,
          speed_kmh: data.speed_kmh, altitude: data.altitude, satellites: data.satellites,
          rssi: data.rssi, is_moving: data.is_moving, gps_timestamp: data.timestamp,
          device_status: 'online', last_seen: data.timestamp, imei: data.imei,
          ...(prev[data.vehicle_id]?.license_plate ? {
            license_plate: prev[data.vehicle_id].license_plate,
            model: prev[data.vehicle_id].model, brand: prev[data.vehicle_id].brand,
            driver_name: prev[data.vehicle_id].driver_name,
          } : {}),
        }
      }));
      setLastUpdate(new Date());
    });
    socket.on('gps:status', (data) => {
      setVehicles(prev => {
        const entry = Object.values(prev).find(v => v.imei === data.imei);
        if (!entry) return prev;
        return { ...prev, [entry.vehicle_id]: { ...entry, device_status: data.status, last_seen: new Date().toISOString() } };
      });
    });
    return () => socket.disconnect();
  }, [loadInitial]);

  // ── Derived data ─────────────────────────────────────────────────────────
  const list    = Object.values(vehicles);
  const moving  = list.filter(v => v.is_moving && !isOffline(v.gps_timestamp, v.interval_seconds));
  const stopped = list.filter(v => !v.is_moving && !isOffline(v.gps_timestamp, v.interval_seconds));
  const offline = list.filter(v => isOffline(v.gps_timestamp, v.interval_seconds));
  const selected = selectedId ? vehicles[selectedId] : null;

  function selectVehicle(v) {
    setSelectedId(v.vehicle_id);
    if (v.latitude && v.longitude) setFlyTo([v.latitude, v.longitude]);
    if (view !== 'map') setView('map');
  }

  const defaultCenter = [-20.4697, -54.6201];
  const mapCenter = list[0]?.latitude ? [list[0].latitude, list[0].longitude] : defaultCenter;

  // ════════════════════════════════════════════════════════════════════════
  // REPLAY LOGIC
  // ════════════════════════════════════════════════════════════════════════
  async function loadReplay() {
    if (!replayVehicleId) return;
    setReplayLoading(true);
    stopReplay();
    try {
      // Calcula início/fim do dia no fuso local e converte para UTC ISO
      const localStart = new Date(replayDate + 'T00:00:00');
      const localEnd   = new Date(replayDate + 'T23:59:59');
      const res = await api.get(`/api/tracking/vehicles/${replayVehicleId}/route-replay`, {
        params: {
          startDate: localStart.toISOString(),
          endDate:   localEnd.toISOString(),
        }
      });
      const { points, stats } = res.data.data;
      setReplayPoints(points);
      setReplayStats(stats);
      setReplayIdx(0);
      replayIdxRef.current = 0;
    } catch (e) {
      console.error('Erro ao carregar replay:', e);
    } finally {
      setReplayLoading(false);
    }
  }

  function startReplay() {
    if (replayPoints.length === 0) return;
    if (replayIdxRef.current >= replayPoints.length - 1) {
      replayIdxRef.current = 0;
      setReplayIdx(0);
    }
    setReplayPlaying(true);
    const interval = Math.max(50, 1000 / replaySpeed);
    replayTimer.current = setInterval(() => {
      replayIdxRef.current += 1;
      if (replayIdxRef.current >= replayPoints.length) {
        replayIdxRef.current = replayPoints.length - 1;
        clearInterval(replayTimer.current);
        setReplayPlaying(false);
      }
      setReplayIdx(replayIdxRef.current);
    }, interval);
  }

  function pauseReplay() {
    clearInterval(replayTimer.current);
    setReplayPlaying(false);
  }

  function stopReplay() {
    clearInterval(replayTimer.current);
    setReplayPlaying(false);
    setReplayIdx(0);
    replayIdxRef.current = 0;
  }

  function seekReplay(idx) {
    replayIdxRef.current = idx;
    setReplayIdx(idx);
  }

  useEffect(() => {
    return () => clearInterval(replayTimer.current);
  }, []);

  // ─── Ponto atual do replay ───────────────────────────────────────────────
  const currentPoint = replayPoints[replayIdx] || null;
  const traveledPoints = replayPoints.slice(0, replayIdx + 1);
  const remainingPoints = replayPoints.slice(replayIdx);

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] gap-0">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Rastreamento GPS</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {view === 'replay'
              ? (replayStats ? `${replayStats.total_km} km · ${replayStats.total_points} pontos · vel. máx ${replayStats.max_speed} km/h` : 'Selecione um veículo e data')
              : lastUpdate ? `Atualizado ${formatAgo(lastUpdate)}` : 'Aguardando dados...'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {view !== 'replay' && (
            <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
              socketOk ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                       : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              {socketOk ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {socketOk ? 'Ao vivo' : 'Desconectado'}
            </span>
          )}

          {/* Tabs */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
            {[['map', 'Mapa', MapPin], ['list', 'Lista', Activity], ['replay', 'Replay', RotateCcw], ['zones', 'Zonas', Shield], ['alerts', 'Alertas', Bell]].map(([v, label, Icon]) => (
              <button key={v} onClick={() => setView(v)}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === v ? 'bg-blue-600 text-white'
                             : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}>
                <Icon className="w-3.5 h-3.5" />{label}
                {v === 'alerts' && alertsUnread > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {alertsUnread > 9 ? '9+' : alertsUnread}
                  </span>
                )}
              </button>
            ))}
          </div>

          {view !== 'replay' && (
            <button onClick={loadInitial}
              className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Recarregar">
              <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      {/* ── Stats bar (só no modo live) ── */}
      {view !== 'replay' && (
        <div className="grid grid-cols-4 gap-px bg-gray-200 dark:bg-gray-700 flex-shrink-0">
          {[
            { label: 'Total',        value: list.length,    color: 'text-blue-600',   bg: 'bg-blue-50 dark:bg-blue-900/20' },
            { label: 'Em movimento', value: moving.length,  color: 'text-green-600',  bg: 'bg-green-50 dark:bg-green-900/20' },
            { label: 'Parados',      value: stopped.length, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
            { label: 'Offline',      value: offline.length, color: 'text-gray-500',   bg: 'bg-gray-50 dark:bg-gray-800' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} px-4 py-2 flex items-center gap-3`}>
              <span className={`text-2xl font-bold ${s.color}`}>{s.value}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Conteúdo principal ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ── */}
        {view !== 'replay' && (
          <div className="w-72 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            {list.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-500 p-6 text-center">
                <Truck className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm">Nenhum veículo rastreado</p>
                <p className="text-xs mt-1">Vincule um IMEI em Rastreamento &gt; Dispositivos</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {list.map(v => {
                  const offline_ = isOffline(v.gps_timestamp, v.interval_seconds);
                  const isSel = v.vehicle_id === selectedId;
                  return (
                    <li key={v.vehicle_id} onClick={() => selectVehicle(v)}
                      className={`p-3 cursor-pointer transition-colors ${
                        isSel ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                               : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent'
                      }`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {v.license_plate || v.imei || 'Veículo'}
                        </span>
                        <span className={`flex-shrink-0 w-2 h-2 rounded-full ml-2 ${
                          offline_ ? 'bg-gray-400' : v.is_moving ? 'bg-green-500' : 'bg-yellow-400'
                        }`} />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1.5">{v.brand} {v.model}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-0.5">
                          <Gauge className="w-3 h-3" />{v.speed_kmh != null ? `${Number(v.speed_kmh).toFixed(0)} km/h` : '—'}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Satellite className="w-3 h-3" />{v.satellites ?? '—'}
                        </span>
                        <span className="flex items-center gap-0.5 ml-auto">
                          <Clock className="w-3 h-3" />{formatAgo(v.gps_timestamp)}
                        </span>
                      </div>
                      {v.driver_name && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 truncate">👤 {v.driver_name}</p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openMonitorModal(v.vehicle_id); }}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors"
                          title="Configurar monitoramento">
                          <Settings className="w-3 h-3" /> Monitor
                        </button>
                        {v.imei && (
                          <button
                            onClick={(e) => { e.stopPropagation(); sendBlockCmd(v.imei, !v.is_blocked); }}
                            disabled={blockingImei === v.imei}
                            className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                              v.is_blocked ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-800'
                            } disabled:opacity-50`}
                            title={v.is_blocked ? 'Clique para desbloquear' : 'Clique para bloquear'}>
                            {blockingImei === v.imei
                              ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                              : <span>{v.is_blocked ? '🔒' : '🔓'}</span>
                            }
                            {v.is_blocked ? 'Bloqueado' : 'Livre'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* ── Área principal ── */}
        <div className="flex-1 relative overflow-hidden flex flex-col">

          {/* ════════════════════════════════════════════════════════════════
              VIEW: REPLAY
          ════════════════════════════════════════════════════════════════ */}
          {view === 'replay' && (
            <div className="flex flex-col h-full">

              {/* Controles do replay */}
              <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">

                  {/* Seleção de veículo */}
                  <select
                    value={replayVehicleId}
                    onChange={e => { setReplayVehicleId(e.target.value); setReplayPoints([]); setReplayStats(null); }}
                    className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecionar veículo...</option>
                    {list.map(v => (
                      <option key={v.vehicle_id} value={v.vehicle_id}>
                        {v.license_plate || v.imei} — {v.brand} {v.model}
                      </option>
                    ))}
                  </select>

                  {/* Data */}
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={replayDate}
                      max={todayStr()}
                      onChange={e => { setReplayDate(e.target.value); setReplayPoints([]); setReplayStats(null); }}
                      className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Botão carregar */}
                  <button
                    onClick={loadReplay}
                    disabled={!replayVehicleId || replayLoading}
                    className="px-4 py-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                  >
                    {replayLoading ? 'Carregando...' : 'Carregar'}
                  </button>

                  {/* Separador */}
                  {replayPoints.length > 0 && (
                    <>
                      <div className="w-px h-6 bg-gray-200 dark:bg-gray-600" />

                      {/* Controles de playback */}
                      <div className="flex items-center gap-1">
                        <button onClick={stopReplay}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
                          title="Parar">
                          <Square className="w-4 h-4" />
                        </button>
                        <button
                          onClick={replayPlaying ? pauseReplay : startReplay}
                          className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                          title={replayPlaying ? 'Pausar' : 'Play'}>
                          {replayPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Velocidade */}
                      <div className="flex items-center gap-1">
                        <FastForward className="w-3.5 h-3.5 text-gray-400" />
                        {SPEEDS.map(s => (
                          <button key={s} onClick={() => {
                            setReplaySpeed(s);
                            if (replayPlaying) { pauseReplay(); setTimeout(() => startReplay(), 50); }
                          }}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              replaySpeed === s
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}>{s}×</button>
                        ))}
                      </div>

                      {/* Stats */}
                      {replayStats && (
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 ml-2">
                          <span>📍 {replayStats.total_km} km</span>
                          <span>⏱ {formatDuration(replayStats.duration_min)}</span>
                          <span>🏎 {replayStats.max_speed} km/h max</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Scrubber / progress bar */}
                {replayPoints.length > 0 && (
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-16 text-right">
                      {formatTime(replayPoints[0]?.gps_timestamp)}
                    </span>
                    <div className="flex-1 relative">
                      <input
                        type="range"
                        min={0}
                        max={replayPoints.length - 1}
                        value={replayIdx}
                        onChange={e => seekReplay(parseInt(e.target.value))}
                        className="w-full h-2 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #2563eb ${(replayIdx / (replayPoints.length - 1)) * 100}%, #e5e7eb ${(replayIdx / (replayPoints.length - 1)) * 100}%)`
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-16">
                      {formatTime(replayPoints[replayPoints.length - 1]?.gps_timestamp)}
                    </span>
                    <span className="text-xs font-medium text-blue-600 w-20 text-center">
                      {formatTime(currentPoint?.gps_timestamp)}
                    </span>
                  </div>
                )}
              </div>

              {/* Mapa do replay */}
              <div className="flex-1 relative">
                {replayPoints.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <RotateCcw className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-base font-medium">Replay de Trajeto</p>
                    <p className="text-sm mt-1">Selecione um veículo e data para reproduzir a rota</p>
                  </div>
                ) : (
                  <MapContainer
                    center={defaultCenter}
                    zoom={13}
                    style={{ width: '100%', height: '100%' }}
                    key="replay-map"
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <MapFitBounds points={replayPoints} />

                    {/* Rota completa (cinza) */}
                    <Polyline
                      positions={replayPoints.map(p => [p.latitude, p.longitude])}
                      color="#d1d5db"
                      weight={3}
                      opacity={0.7}
                    />

                    {/* Trecho percorrido (azul) */}
                    {traveledPoints.length > 1 && (
                      <Polyline
                        positions={traveledPoints.map(p => [p.latitude, p.longitude])}
                        color="#2563eb"
                        weight={4}
                        opacity={0.9}
                      />
                    )}

                    {/* Ponto de início */}
                    <Marker
                      position={[replayPoints[0].latitude, replayPoints[0].longitude]}
                      icon={L.divIcon({
                        html: '<div style="width:12px;height:12px;border-radius:50%;background:#16a34a;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
                        className: '', iconSize: [12, 12], iconAnchor: [6, 6]
                      })}
                    >
                      <Popup><b>Início</b><br />{formatTime(replayPoints[0].gps_timestamp)}</Popup>
                    </Marker>

                    {/* Ponto de fim */}
                    <Marker
                      position={[replayPoints[replayPoints.length-1].latitude, replayPoints[replayPoints.length-1].longitude]}
                      icon={L.divIcon({
                        html: '<div style="width:12px;height:12px;border-radius:50%;background:#dc2626;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>',
                        className: '', iconSize: [12, 12], iconAnchor: [6, 6]
                      })}
                    >
                      <Popup><b>Fim</b><br />{formatTime(replayPoints[replayPoints.length-1].gps_timestamp)}</Popup>
                    </Marker>

                    {/* Veículo animado */}
                    {currentPoint && (
                      <Marker
                        position={[currentPoint.latitude, currentPoint.longitude]}
                        icon={makeTruckIcon(Number(currentPoint.speed_kmh) > 5, false, '#7c3aed')}
                      >
                        <Popup>
                          <div className="text-sm space-y-1">
                            <p><b>{formatTime(currentPoint.gps_timestamp)}</b></p>
                            <p>Velocidade: <b>{Number(currentPoint.speed_kmh || 0).toFixed(0)} km/h</b></p>
                            <p>Satélites: <b>{currentPoint.satellites ?? '—'}</b></p>
                            {currentPoint.altitude_meters && <p>Altitude: <b>{Number(currentPoint.altitude_meters).toFixed(0)}m</b></p>}
                            <p className="text-xs text-gray-400">Ponto {replayIdx + 1} / {replayPoints.length}</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                )}

                {/* Info card overlay do ponto atual */}
                {currentPoint && replayPoints.length > 0 && (
                  <div className="absolute top-3 right-3 z-[1000] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 min-w-[160px]">
                    <p className="text-xs text-gray-500 mb-1">Ponto {replayIdx + 1}/{replayPoints.length}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {Number(currentPoint.speed_kmh || 0).toFixed(0)} <span className="text-sm font-normal text-gray-500">km/h</span>
                    </p>
                    <p className="text-xs text-blue-600 font-medium mt-1">{formatTime(currentPoint.gps_timestamp)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {Number(currentPoint.latitude).toFixed(5)}, {Number(currentPoint.longitude).toFixed(5)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              VIEW: MAPA LIVE
          ════════════════════════════════════════════════════════════════ */}
          {view === 'map' && (
            <MapContainer center={mapCenter} zoom={13} style={{ width: '100%', height: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {flyTo && <MapFlyTo position={flyTo} />}
              {list.map(v => {
                if (!v.latitude || !v.longitude) return null;
                const offline_ = isOffline(v.gps_timestamp, v.interval_seconds);
                return (
                  <Marker key={v.vehicle_id} position={[v.latitude, v.longitude]}
                    icon={makeTruckIcon(v.is_moving, offline_)}
                    eventHandlers={{ click: () => selectVehicle(v) }}>
                    <Popup minWidth={200}>
                      <div className="text-sm">
                        <p className="font-bold text-base mb-1">🚛 {v.license_plate || 'Sem placa'}</p>
                        <p className="text-gray-500 text-xs mb-2">{v.brand} {v.model}</p>
                        <div className="space-y-1 text-xs">
                          {[
                            ['Velocidade', `${Number(v.speed_kmh || 0).toFixed(0)} km/h`],
                            ['Satélites', v.satellites ?? '—'],
                            ['Sinal 4G', v.rssi != null ? `${v.rssi} dBm` : '—'],
                            ['Altitude', v.altitude ? `${Number(v.altitude).toFixed(0)}m` : '—'],
                            ['IMEI', v.imei || '—'],
                            ['Atualizado', formatAgo(v.gps_timestamp)],
                            ...(v.driver_name ? [['Motorista', v.driver_name]] : []),
                          ].map(([k, val]) => (
                            <div key={k} className="flex justify-between">
                              <span className="text-gray-500">{k}</span>
                              <span className="font-medium">{val}</span>
                            </div>
                          ))}
                        </div>
                        <div className={`mt-2 px-2 py-1 rounded text-xs font-medium text-center ${
                          offline_ ? 'bg-gray-100 text-gray-500' :
                          v.is_moving ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {offline_ ? '● Offline' : v.is_moving ? '● Em movimento' : '● Parado'}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}

          {/* ════════════════════════════════════════════════════════════════
              VIEW: LISTA
          ════════════════════════════════════════════════════════════════ */}
          {view === 'list' && (
            <div className="overflow-auto h-full bg-white dark:bg-gray-900">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    {['Veículo','Status','Velocidade','Coordenadas','Satélites','Sinal 4G','Motorista','Atualizado','Bloqueio'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {list.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-gray-400">Nenhum veículo rastreado ainda</td></tr>
                  ) : list.map(v => {
                    const offline_ = isOffline(v.gps_timestamp, v.interval_seconds);
                    return (
                      <tr key={v.vehicle_id} onClick={() => selectVehicle(v)} className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900 dark:text-white">{v.license_plate || '—'}</p>
                          <p className="text-xs text-gray-400">{v.brand} {v.model}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            offline_ ? 'bg-gray-100 text-gray-500' : v.is_moving ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {offline_ ? 'Offline' : v.is_moving ? 'Em movimento' : 'Parado'}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">{Number(v.speed_kmh || 0).toFixed(0)} km/h</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">
                          {v.latitude ? `${Number(v.latitude).toFixed(5)}, ${Number(v.longitude).toFixed(5)}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">{v.satellites ?? '—'}</td>
                        <td className="px-4 py-3 text-center">{v.rssi ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{v.driver_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatAgo(v.gps_timestamp)}</td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          {v.imei ? (
                            <button
                              onClick={() => sendBlockCmd(v.imei, !v.is_blocked)}
                              disabled={blockingImei === v.imei}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                v.is_blocked
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300'
                              } disabled:opacity-50`}
                            >
                              {blockingImei === v.imei ? (
                                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <span>{v.is_blocked ? '🔒' : '🔓'}</span>
                              )}
                              {v.is_blocked ? 'Bloqueado' : 'Livre'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">Sem GPS</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* ════════════════════════════════════════════════════════════════
              VIEW: ZONAS (Geofencing)
          ════��═══════════════════════════════════════════════════════════ */}
          {view === 'zones' && (
            <div className="flex h-full overflow-hidden">
              {/* Painel esquerdo */}
              <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="font-semibold text-sm text-gray-900 dark:text-white">Zonas Configuradas</span>
                  <button
                    onClick={() => { setDrawingMode(d => !d); setSelectedZone(null); }}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      drawingMode ? 'bg-red-100 text-red-700 dark:bg-red-900/30' : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}>
                    {drawingMode ? <><X className="w-3 h-3" /> Cancelar</> : <><Plus className="w-3 h-3" /> Nova Zona</>}
                  </button>
                </div>

                {/* Formulário nova zona */}
                {drawingMode && (
                  <div className="p-3 border-b border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                      {newZoneForm.lat ? '✅ Clique no mapa definido. Preencha e salve.' : '👆 Clique no mapa para definir o centro'}
                    </p>
                    <input
                      placeholder="Nome da zona *"
                      value={newZoneForm.name}
                      onChange={e => setNewZoneForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full mb-2 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <div className="flex gap-2 mb-2">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">Raio (metros)</label>
                        <input
                          type="number" min={50} max={50000} step={50}
                          value={newZoneForm.radius_meters}
                          onChange={e => setNewZoneForm(p => ({ ...p, radius_meters: e.target.value }))}
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Cor</label>
                        <div className="flex gap-1 mt-1">
                          {ZONE_COLORS.map(c => (
                            <button key={c} onClick={() => setNewZoneForm(p => ({ ...p, color: c }))}
                              className={`w-5 h-5 rounded-full border-2 transition-all ${newZoneForm.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                              style={{ background: c }} />
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={createGeofence}
                      disabled={!newZoneForm.name || !newZoneForm.lat}
                      className="w-full py-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors">
                      Salvar Zona
                    </button>
                  </div>
                )}

                {/* Lista de zonas */}
                <div className="flex-1 overflow-y-auto">
                  {geofences.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6 text-center">
                      <Shield className="w-10 h-10 mb-3 opacity-30" />
                      <p className="text-sm">Nenhuma zona criada</p>
                      <p className="text-xs mt-1">Clique em "Nova Zona" e marque o centro no mapa</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                      {geofences.map((g, i) => (
                        <li key={g.id}
                          onClick={() => { setSelectedZone(g); loadZoneVehicles(g.id); setDrawingMode(false); }}
                          className={`p-3 cursor-pointer transition-colors ${
                            selectedZone?.id === g.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: g.color || '#3B82F6' }} />
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{g.name}</span>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); deleteGeofence(g.id); }}
                              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-gray-400 hover:text-red-600 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5 ml-5">
                            {g.type === 'circular' ? `Círculo · ${Number(g.radius_meters).toLocaleString('pt-BR')}m raio` : 'Polígono'}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Painel de zona selecionada */}
                {selectedZone && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                      <Link className="w-3.5 h-3.5" /> Vincular veículo a "{selectedZone.name}"
                    </p>
                    <select
                      value={linkVehicleId}
                      onChange={e => setLinkVehicleId(e.target.value)}
                      className="w-full mb-2 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="">Selecionar veículo...</option>
                      {list.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.license_plate || v.imei} — {v.brand} {v.model}</option>)}
                    </select>
                    <div className="flex gap-3 mb-2 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={linkAllowed} onChange={e => setLinkAllowed(e.target.checked)} className="rounded" />
                        Zona autorizada
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={linkAlertEntry} onChange={e => setLinkAlertEntry(e.target.checked)} className="rounded" />
                        Alerta entrada
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={linkAlertExit} onChange={e => setLinkAlertExit(e.target.checked)} className="rounded" />
                        Alerta saída
                      </label>
                    </div>
                    <button
                      onClick={() => linkVehicleToZone(selectedZone.id)}
                      disabled={!linkVehicleId}
                      className="w-full py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg transition-colors">
                      Vincular
                    </button>
                    {zoneVehicles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-500">Veículos vinculados:</p>
                        {zoneVehicles.map(vg => (
                          <div key={vg.vehicle_id} className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
                            <span>{vg.license_plate || vg.vehicle_id}</span>
                            <span className="text-gray-400">{vg.is_allowed_zone ? '✅ autorizado' : '⚠️ restrito'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mapa de zonas */}
              <div className="flex-1 relative">
                <MapContainer center={defaultCenter} zoom={12} style={{ width: '100%', height: '100%' }} key="zones-map">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {drawingMode && (
                    <MapClickCapture onMapClick={(latlng) => {
                      setNewZoneForm(p => ({ ...p, lat: latlng.lat, lng: latlng.lng }));
                    }} />
                  )}
                  {/* Zonas existentes */}
                  {geofences.map(g => g.center_latitude && (
                    <Circle
                      key={g.id}
                      center={[Number(g.center_latitude), Number(g.center_longitude)]}
                      radius={Number(g.radius_meters)}
                      pathOptions={{ color: g.color || '#3B82F6', fillColor: g.color || '#3B82F6', fillOpacity: 0.15, weight: 2 }}
                      eventHandlers={{ click: () => { setSelectedZone(g); loadZoneVehicles(g.id); setDrawingMode(false); } }}>
                      <Popup><b>{g.name}</b><br />Raio: {Number(g.radius_meters).toLocaleString('pt-BR')}m</Popup>
                    </Circle>
                  ))}
                  {/* Preview da nova zona */}
                  {drawingMode && newZoneForm.lat && (
                    <Circle
                      center={[newZoneForm.lat, newZoneForm.lng]}
                      radius={Number(newZoneForm.radius_meters)}
                      pathOptions={{ color: newZoneForm.color, fillColor: newZoneForm.color, fillOpacity: 0.2, dashArray: '8 4', weight: 2 }}
                    />
                  )}
                  {/* Veículos */}
                  {list.map(v => v.latitude && (
                    <Marker key={v.vehicle_id} position={[v.latitude, v.longitude]}
                      icon={makeTruckIcon(v.is_moving, isOffline(v.gps_timestamp, v.interval_seconds))}>
                      <Popup>{v.license_plate || v.imei}</Popup>
                    </Marker>
                  ))}
                </MapContainer>
                {drawingMode && !newZoneForm.lat && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg">
                    👆 Clique no mapa para posicionar a zona
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              VIEW: ALERTAS
          ════════════════════════════════════════════════════════════════ */}
          {view === 'alerts' && (
            <div className="flex flex-col h-full bg-white dark:bg-gray-900 overflow-hidden">
              {/* Filtros */}
              <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <select
                  value={alertFilter.type}
                  onChange={e => setAlertFilter(p => ({ ...p, type: e.target.value }))}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="">Todos os tipos</option>
                  {Object.entries(ALERT_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <select
                  value={alertFilter.resolved}
                  onChange={e => setAlertFilter(p => ({ ...p, resolved: e.target.value }))}
                  className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="false">Não resolvidos</option>
                  <option value="true">Resolvidos</option>
                  <option value="">Todos</option>
                </select>
                {alertsUnread > 0 && (
                  <button onClick={readAllAlerts}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Marcar todos como lidos
                  </button>
                )}
              </div>

              {/* Lista de alertas */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <CheckCircle className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-base font-medium">Nenhum alerta</p>
                    <p className="text-sm mt-1 text-center">Os alertas aparecerão aqui conforme as regras de monitoramento</p>
                  </div>
                ) : alerts.map(alert => {
                  const meta = ALERT_META[alert.alert_type] || { label: alert.alert_type, icon: '⚠️', color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200 dark:border-gray-600' };
                  const ts = alert.created_at;
                  return (
                    <div key={alert.id} className={`rounded-xl border p-4 ${meta.bg} ${meta.border} ${!alert.is_read ? 'ring-1 ring-blue-400' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <span className="text-xl flex-shrink-0 mt-0.5">{meta.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${meta.color}`}>{alert.title}</span>
                              {!alert.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                              }`}>{alert.severity}</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              🚛 {alert.license_plate || '—'} {alert.brand} {alert.model}
                              {alert.driver_name ? ` · 👤 ${alert.driver_name}` : ''}
                            </p>
                            {alert.description && <p className="text-xs text-gray-500 mt-1">{alert.description}</p>}
                            {alert.speed_kmh && <p className="text-xs text-gray-400 mt-0.5">Velocidade: {Number(alert.speed_kmh).toFixed(0)} km/h</p>}
                            <p className="text-xs text-gray-400 mt-1">{formatAgo(ts)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!alert.is_read && (
                            <button onClick={() => markAlertRead(alert.id)}
                              className="p-1.5 rounded-lg hover:bg-white/50 text-gray-400 hover:text-gray-600 transition-colors" title="Marcar como lido">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {!alert.is_resolved && (
                            <button onClick={() => resolveAlert(alert.id)}
                              className="p-1.5 rounded-lg hover:bg-white/50 text-gray-400 hover:text-green-600 transition-colors" title="Resolver">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          MODAL: Configuração de Monitoramento por Veículo
      ════════════════════════════════════════════════════════════════ */}
      {monitorVehicleId && monitorConfig && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">Configurar Monitoramento</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {list.find(v => v.vehicle_id === monitorVehicleId)?.license_plate || monitorVehicleId}
                </p>
              </div>
              <button onClick={() => setMonitorVehicleId(null)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Velocidade e tempos */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Limite de velocidade</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min={20} max={200} step={5}
                      value={monitorConfig.speed_limit_kmh || 80}
                      onChange={e => setMonitorConfig(p => ({ ...p, speed_limit_kmh: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <span className="text-xs text-gray-400 whitespace-nowrap">km/h</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Parado fora zona</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min={5} max={240} step={5}
                      value={monitorConfig.max_idle_minutes || 30}
                      onChange={e => setMonitorConfig(p => ({ ...p, max_idle_minutes: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <span className="text-xs text-gray-400">min</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Offline após</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min={5} max={120} step={5}
                      value={monitorConfig.offline_alert_minutes || 15}
                      onChange={e => setMonitorConfig(p => ({ ...p, offline_alert_minutes: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <span className="text-xs text-gray-400">min</span>
                  </div>
                </div>
              </div>

              {/* Horário de trabalho */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Horário de trabalho permitido</label>
                <div className="flex items-center gap-2">
                  <input type="time"
                    value={(monitorConfig.work_hours_start || '07:00:00').slice(0,5)}
                    onChange={e => setMonitorConfig(p => ({ ...p, work_hours_start: e.target.value }))}
                    className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  <span className="text-gray-400">até</span>
                  <input type="time"
                    value={(monitorConfig.work_hours_end || '18:00:00').slice(0,5)}
                    onChange={e => setMonitorConfig(p => ({ ...p, work_hours_end: e.target.value }))}
                    className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div className="flex gap-2 mt-2">
                  {[['Dom','0'],['Seg','1'],['Ter','2'],['Qua','3'],['Qui','4'],['Sex','5'],['Sáb','6']].map(([d, n]) => {
                    const days = monitorConfig.work_days || [1,2,3,4,5];
                    const active = days.includes(parseInt(n));
                    return (
                      <button key={n} onClick={() => {
                        const days2 = active ? days.filter(x => x !== parseInt(n)) : [...days, parseInt(n)];
                        setMonitorConfig(p => ({ ...p, work_days: days2 }));
                      }}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          active ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>{d}</button>
                    );
                  })}
                </div>
              </div>

              {/* Toggles de alertas */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">Alertas ativos</label>
                <div className="space-y-2">
                  {[
                    ['alert_speed',             '🏎 Velocidade excessiva'],
                    ['alert_idle',              '🅿️ Parado em local indesejado'],
                    ['alert_offline',           '📡 Veículo offline'],
                    ['alert_outside_work_hours','🕐 Em movimento fora do horário'],
                    ['alert_outside_zones',     '📍 Fora das zonas autorizadas'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                      <div
                        onClick={() => setMonitorConfig(p => ({ ...p, [key]: !p[key] }))}
                        className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${
                          monitorConfig[key] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          monitorConfig[key] ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setMonitorVehicleId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
                Cancelar
              </button>
              <button onClick={saveMonitorConfig}
                className="px-5 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors">
                Salvar Configuração
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
